import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Play, 
  Pause, 
  CheckCircle, 
  Clock, 
  Dumbbell,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { WorkoutService } from '../lib/workoutService';
import { WorkoutSession as WorkoutSessionType } from '../types/dataModel';
import { useCachedData } from '../hooks/useCachedData';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PAGE_CHROME_LOADER_BRAND_GREEN, PAGE_CHROME_OFF_WHITE } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

export const WorkoutSession = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [session, setSession] = useState<(WorkoutSessionType & { workout?: any }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStarted, setIsStarted] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);

  usePageStatusBar({
    background: loading ? PAGE_CHROME_LOADER_BRAND_GREEN : PAGE_CHROME_OFF_WHITE,
  });

  // Fetch session data with caching
  const fetchSessionData = useCallback(async () => {
    if (!id) {
      throw new Error('Session ID is required');
    }

    const sessionData = await WorkoutService.getWorkoutSessionById(id);
    
    if (!sessionData) {
      throw new Error('Workout session not found');
    }

    // Check if session is already started
    if (sessionData.status === 'in_progress' && sessionData.startedAt) {
      setIsStarted(true);
      setStartTime(new Date(sessionData.startedAt));
    }

    return sessionData;
  }, [id]);

  // Use cached data hook
  const {
    data: sessionData,
    loading: dataLoading,
    error: dataError,
    refetch,
  } = useCachedData({
    cacheKey: `workout_session_${id}`,
    fetchFn: fetchSessionData,
    ttl: 10 * 60 * 1000, // 10 minutes for workout sessions
    staleWhileRevalidate: true,
    enabled: !!id,
  });

  // Update session state when data is available
  useEffect(() => {
    if (sessionData) {
      setSession(sessionData);
    }
  }, [sessionData]);

  // Set loading and error states
  useEffect(() => {
    setLoading(dataLoading);
    setError(dataError?.message || null);
  }, [dataLoading, dataError]);

  const handleStartWorkout = async () => {
    if (!session || !id) return;

    try {
      // Update session status to in_progress
      await WorkoutService.updateWorkoutSessionStatus(id, 'in_progress');
      
      setIsStarted(true);
      setStartTime(new Date());
      
      // Update local session state
      setSession({
        ...session,
        status: 'in_progress',
        startedAt: new Date().toISOString()
      });
    } catch (err: any) {
      console.error('Error starting workout:', err);
      setError(err.message || 'Failed to start workout');
    }
  };

  const handleCompleteWorkout = async () => {
    if (!session || !id || !startTime) return;

    try {
      const durationMinutes = Math.floor((Date.now() - startTime.getTime()) / 60000);
      
      // Update session status to completed
      await WorkoutService.updateWorkoutSessionStatus(id, 'completed', {
        completedAt: new Date().toISOString(),
        actualDurationMinutes: durationMinutes
      });
      
      // Navigate back to dashboard
      navigate('/member-dashboard');
    } catch (err: any) {
      console.error('Error completing workout:', err);
      setError(err.message || 'Failed to complete workout');
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Workout session not found'}</p>
          <button
            onClick={() => navigate('/member-dashboard')}
            className="px-6 py-3 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg font-medium hover:bg-[var(--action-primary-pressed)] transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/member-dashboard')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">
            {session.workout?.name || 'Workout'}
          </h1>
          {session.workout?.note && (
            <p className="text-gray-600">{session.workout.note}</p>
          )}
        </div>

        {/* Workout Status Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                {isStarted ? 'Workout in Progress' : 'Ready to Start'}
              </h2>
              {isStarted && startTime && (
                <p className="text-sm text-gray-600">
                  Started at {startTime.toLocaleTimeString()}
                </p>
              )}
            </div>
            {!isStarted ? (
              <button
                onClick={handleStartWorkout}
                className="inline-flex items-center px-6 py-3 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg font-medium hover:bg-[var(--action-primary-pressed)] transition-colors"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Workout
              </button>
            ) : (
              <button
                onClick={handleCompleteWorkout}
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Complete Workout
              </button>
            )}
          </div>
        </div>

        {/* Workout Content */}
        {session.workout?.workoutBlocks && session.workout.workoutBlocks.length > 0 ? (
          <div className="space-y-4">
            {session.workout.workoutBlocks.map((block: any, blockIndex: number) => (
              <div
                key={block.id || blockIndex}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {block.name || `Block ${blockIndex + 1}`}
                </h3>
                {block.exerciseSets && block.exerciseSets.length > 0 && (
                  <div className="space-y-4">
                    {block.exerciseSets.map((set: any, setIndex: number) => (
                      <div
                        key={set.id || setIndex}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                              {set.exercise?.name || 'Exercise'}
                            </h4>
                            {set.exercise?.description && (
                              <p className="text-sm text-gray-600 mb-2">
                                {set.exercise.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                          {/* NEW SCHEMA: Sets are in block, durationSeconds for time-based exercises */}
                          {set.durationSeconds ? (
                            <div>
                              <span className="text-gray-600">Duration:</span>
                              <span className="ml-2 font-medium text-gray-900">{set.durationSeconds} seconds</span>
                            </div>
                          ) : (
                            <>
                              {block.sets && (
                                <div>
                                  <span className="text-gray-600">Sets:</span>
                                  <span className="ml-2 font-medium text-gray-900">{block.sets}</span>
                                </div>
                              )}
                              {((set as any).repText && String((set as any).repText).trim()) || set.rep ? (
                                <div>
                                  <span className="text-gray-600">Reps:</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {(set as any).repText && String((set as any).repText).trim()
                                      ? String((set as any).repText).trim()
                                      : set.rep}
                                  </span>
                                </div>
                              ) : null}
                            </>
                          )}
                          {set.restSeconds && set.restSeconds > 0 && (
                            <div>
                              <span className="text-gray-600">Rest:</span>
                              <span className="ml-2 font-medium text-gray-900">{set.restSeconds}s</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Dumbbell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Exercises Found
            </h3>
            <p className="text-gray-600">
              This workout doesn't have any exercises configured yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

